import React, { useState, Fragment } from 'react';
import { 
  CheckCircleIcon, 
  XCircleIcon,
  CalendarIcon,
  MapPinIcon,
  ClipboardIcon,
  LinkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { EstablishmentStatusChange } from '../../types/surveillance';
import { LinkedAdherent } from '../../store/slices/nouveauxAdherentsSlice';
import { formatDate } from '../../utils/dateUtils';
import NotesButton from '../notes/NotesButton';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';
import LinkedAdherentsPopup from '../nouveauxAdherents/LinkedAdherentsPopup';

interface SurveillanceTableProps {
  establishments: EstablishmentStatusChange[];
  isLoading?: boolean;
}

interface NDWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionType: 'map' | 'copy';
}

const NDWarningModal: React.FC<NDWarningModalProps> = ({ isOpen, onClose, onConfirm, actionType }) => {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      Données limitées
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Certaines informations de cette entreprise sont marquées comme non divulguées [ND] dans les données ouvertes.
                        {actionType === 'map' 
                          ? " L'adresse est donc très minimaliste et peut ne pas être précise sur Google Maps." 
                          : " L'adresse copiée peut être incomplète."}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Nous ne pouvons pas fournir plus d'informations que celles déjà affichées.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    onClick={() => {
                      onClose();
                      onConfirm();
                    }}
                  >
                    Continuer quand même
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                  >
                    Annuler
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const SurveillanceTable: React.FC<SurveillanceTableProps> = ({
  establishments,
  isLoading = false
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedAdherents, setSelectedAdherents] = useState<LinkedAdherent[]>([]);
  const [copyStatus, setCopyStatus] = useState<{[key: string]: boolean}>({});
  const [showNDWarning, setShowNDWarning] = useState<boolean>(false);
  const [ndWarningType, setNdWarningType] = useState<'map' | 'copy'>('map');
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (establishments.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.26-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Aucun changement d'établissement trouvé
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Essayez de modifier vos critères de recherche.
          </p>
        </div>
      </div>
    );
  }

  const getStatusIcon = (statusChange: 'opened' | 'closed') => {
    return statusChange === 'opened' ? (
      <CheckCircleIcon className="h-5 w-5 text-green-500" />
    ) : (
      <XCircleIcon className="h-5 w-5 text-red-500" />
    );
  };

  const getStatusText = (statusChange: 'opened' | 'closed') => {
    return statusChange === 'opened' ? 'Ouverture' : 'Fermeture';
  };

  const getStatusBadgeClasses = (statusChange: 'opened' | 'closed') => {
    return statusChange === 'opened'
      ? 'bg-green-100 text-green-800 ring-green-600/20'
      : 'bg-red-100 text-red-800 ring-red-600/20';
  };

  // Check if address has ND values
  const hasNDValues = (establishment: EstablishmentStatusChange) => {
    return [
      establishment.numeroVoie,
      establishment.typeVoie,
      establishment.libelleVoie,
      establishment.codePostalEtablissement,
      establishment.libelleCommuneEtablissement
    ].some(value => value === '[ND]');
  };

  // Get formatted address for copying
  const getFormattedAddress = (establishment: EstablishmentStatusChange) => {
    const addressComponents = [
      establishment.numeroVoie !== '[ND]' ? establishment.numeroVoie : null,
      establishment.typeVoie !== '[ND]' ? establishment.typeVoie : null,
      establishment.libelleVoie !== '[ND]' ? establishment.libelleVoie : null,
    ].filter(Boolean).join(' ');
    
    const codePostal = establishment.codePostalEtablissement !== '[ND]' ? establishment.codePostalEtablissement : '';
    const commune = establishment.libelleCommuneEtablissement !== '[ND]' ? establishment.libelleCommuneEtablissement : '';
    const cityInfo = `${codePostal} ${commune}`.trim();
    
    return `${addressComponents}\n${cityInfo}`;
  };

  // Get Google Maps URL for the address
  const getGoogleMapsUrl = (establishment: EstablishmentStatusChange) => {
    const addressParts = [
      [
        establishment.numeroVoie !== '[ND]' ? establishment.numeroVoie : null,
        establishment.typeVoie !== '[ND]' ? establishment.typeVoie : null,
        establishment.libelleVoie !== '[ND]' ? establishment.libelleVoie : null,
      ].filter(Boolean).join(' '),
      establishment.codePostalEtablissement !== '[ND]' ? establishment.codePostalEtablissement : null,
      establishment.libelleCommuneEtablissement !== '[ND]' ? establishment.libelleCommuneEtablissement : null
    ].filter(Boolean).join(', ');
    
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts)}`;
  };

  // Handle Google Maps click with warning
  const handleGoogleMapsClick = (e: React.MouseEvent, establishment: EstablishmentStatusChange) => {
    e.preventDefault();
    
    if (hasNDValues(establishment)) {
      setNdWarningType('map');
      setShowNDWarning(true);
      setPendingAction(() => () => {
        window.open(getGoogleMapsUrl(establishment), '_blank');
      });
    } else {
      window.open(getGoogleMapsUrl(establishment), '_blank');
    }
  };

  // Handle address copy with warning
  const handleAddressCopy = (establishment: EstablishmentStatusChange) => {
    if (hasNDValues(establishment)) {
      setNdWarningType('copy');
      setShowNDWarning(true);
      setPendingAction(() => () => {
        copyAddressToClipboard(establishment);
      });
    } else {
      copyAddressToClipboard(establishment);
    }
  };

  const copyAddressToClipboard = (establishment: EstablishmentStatusChange) => {
    const address = getFormattedAddress(establishment);
    navigator.clipboard.writeText(address).then(
      () => {
        setCopyStatus({ ...copyStatus, [`address-${establishment.siret}`]: true });
        
        // Reset the copy status after 2 seconds
        setTimeout(() => {
          setCopyStatus({ ...copyStatus, [`address-${establishment.siret}`]: false });
        }, 2000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  const formatAddress = (establishment: EstablishmentStatusChange) => {
    const parts = [];
    if (establishment.numeroVoie && establishment.numeroVoie.trim() !== '' && establishment.numeroVoie !== '[ND]') {
      parts.push(establishment.numeroVoie);
    }
    if (establishment.typeVoie && establishment.typeVoie.trim() !== '' && establishment.typeVoie !== '[ND]') {
      parts.push(establishment.typeVoie);
    }
    if (establishment.libelleVoie && establishment.libelleVoie.trim() !== '' && establishment.libelleVoie !== '[ND]') {
      parts.push(establishment.libelleVoie);
    }
    
    const street = parts.join(' ');
    
    const locationParts = [];
    if (establishment.codePostalEtablissement && establishment.codePostalEtablissement.trim() !== '' && establishment.codePostalEtablissement !== '[ND]') {
      locationParts.push(establishment.codePostalEtablissement);
    }
    if (establishment.libelleCommuneEtablissement && establishment.libelleCommuneEtablissement.trim() !== '' && establishment.libelleCommuneEtablissement !== '[ND]') {
      locationParts.push(establishment.libelleCommuneEtablissement);
    }
    
    const location = locationParts.join(' ');
    
    // Check if we have any valid address information
    const hasValidAddress = street.trim() !== '' || location.trim() !== '';
    
    return { street, location, hasValidAddress };
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        showSuccessToast('SIRET copié dans le presse-papier', 'siret-copied');
      })
      .catch((err) => {
        showErrorToast('Erreur lors de la copie du SIRET', 'siret-copy-error');
        console.error('Erreur lors de la copie :', err);
      });
  };

  const handleShowLinkedAdherents = (surveillanceAdherents: EstablishmentStatusChange['linkedAdherents']) => {
    if (!surveillanceAdherents) return;
    
    // Convert surveillance LinkedAdherents to the format expected by LinkedAdherentsPopup
    const convertedAdherents: LinkedAdherent[] = surveillanceAdherents.map(adherent => ({
      id: adherent.id,
      name: adherent.name || 'Contact non spécifié',
      siret: adherent.siret,
      // Note: address is not available in surveillance data, so we omit it
    }));
    
    setSelectedAdherents(convertedAdherents);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedAdherents([]);
  };

  // Function to render linked adherents column
  const renderLinkedAdherents = (establishment: EstablishmentStatusChange) => {
    if (!establishment.linkedAdherents || establishment.linkedAdherents.length === 0) {
      return (
        <span className="inline-flex items-center text-gray-500 px-3 py-2 text-sm">
          <svg className="h-4 w-4 mr-1.5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          Aucun adhérent lié
        </span>
      );
    }

    if (establishment.linkedAdherents.length === 1) {
      const adherent = establishment.linkedAdherents[0];
      return (
        <div className="border border-blue-200 rounded-md p-3 bg-blue-50">
          <div className="font-medium text-gray-900">{adherent.name || 'Contact non spécifié'}</div>
          {adherent.email && (
            <div className="text-xs text-gray-500 mt-1">{adherent.email}</div>
          )}
          <div className="text-xs text-gray-400 mt-1">SIRET: {adherent.siret}</div>
          <Link 
            to={`/adherents/${adherent.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 mt-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Voir l'adhérent
          </Link>
        </div>
      );
    }

    return (
      <button
        onClick={() => handleShowLinkedAdherents(establishment.linkedAdherents!)}
        className="inline-flex items-center rounded-md bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
      >
        <LinkIcon className="h-4 w-4 mr-1.5" />
        {`${establishment.linkedAdherents.length} adhérents liés`}
      </button>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Historique des changements des établissements de vos adhérents
        </h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 min-w-[200px] max-w-[300px]">
                  Établissement
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[120px]">
                  Statut
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[140px]">
                  Date du changement
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 min-w-[180px] max-w-[250px]">
                  Adresse
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 min-w-[180px]">
                  Adhérents liés
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {establishments.map((establishment, index) => {
                const { street, location, hasValidAddress } = formatAddress(establishment);
                
                return (
                  <tr key={`${establishment.siret}-${index}`} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="max-w-[300px]">
                        <div className="flex items-center space-x-2">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {establishment.name}
                          </div>
                          <NotesButton
                            siren={establishment.siren}
                            companyName={establishment.name}
                            variant="icon"
                            size="sm"
                          />
                        </div>
                        <div className="flex items-center text-xs text-gray-600 mt-1">
                          <span className="truncate">SIRET: {establishment.siret}</span>
                          <button 
                            onClick={() => copyToClipboard(establishment.siret)}
                            className="ml-2 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                            title="Copier le SIRET"
                          >
                            <ClipboardIcon className="h-4 w-4 text-gray-500" />
                          </button>
                        </div>
                        {establishment.activitePrincipaleEtablissement && (
                          <div className="text-xs text-gray-400 mt-1 truncate">
                            NAF: {establishment.activitePrincipaleEtablissement}
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(establishment.statusChange)}
                        <span className={`ml-2 inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStatusBadgeClasses(establishment.statusChange)}`}>
                          {getStatusText(establishment.statusChange)}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <CalendarIcon className="h-4 w-4 mr-1 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{formatDate(establishment.dateDebut)}</span>
                      </div>
                    </td>
                    
                    <td className="px-4 py-4">
                      <div className="max-w-[250px]">
                        <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border border-gray-200 relative group">
                          {hasValidAddress ? (
                            <>
                              <div className="flex items-center justify-center">
                                <div className="flex flex-col space-y-1 mt-0.5 flex-shrink-0">
                                  <a
                                    href="#"
                                    onClick={(e) => handleGoogleMapsClick(e, establishment)}
                                    className="p-1 rounded-md hover:bg-gray-200 transition-colors text-blue-500 hover:text-blue-700"
                                    title="Voir sur Google Maps"
                                  >
                                    <MapPinIcon className="h-4 w-4" />
                                  </a>
                                  <button
                                    onClick={() => handleAddressCopy(establishment)}
                                    className="p-1 rounded-md hover:bg-gray-200 transition-colors"
                                    title="Copier l'adresse"
                                  >
                                    <ClipboardIcon className="h-4 w-4 text-gray-500" />
                                  </button>
                                </div>
                                <div className="ml-2 min-w-0 flex-1">
                                  {street && <div className="truncate">{street}</div>}
                                  {location && <div className="text-gray-500 truncate">{location}</div>}
                                </div>
                              </div>
                              {copyStatus[`address-${establishment.siret}`] && (
                                <span className="absolute top-2 right-2 text-xs text-green-600 bg-white px-1 py-0.5 rounded">Copié !</span>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center justify-center">
                              <MapPinIcon className="h-4 w-4 mr-1 text-gray-400 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-500">Adresse non disponible</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <div className="min-w-0">
                        {renderLinkedAdherents(establishment)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {showPopup && (
        <LinkedAdherentsPopup 
          adherents={selectedAdherents} 
          onClose={handleClosePopup} 
        />
      )}

      {/* Add the warning modal */}
      <NDWarningModal 
        isOpen={showNDWarning} 
        onClose={() => setShowNDWarning(false)}
        onConfirm={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        actionType={ndWarningType}
      />
    </div>
  );
};

export default SurveillanceTable; 